import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Check,
  Package,
  Truck,
  MapPin,
  Sparkles,
  Loader2,
  ExternalLink,
  ArrowLeft,
  Clock,
  AlertTriangle,
  MessageCircle,
  Headphones,
} from "lucide-react";
import { resolveTrackingState, isSafeTrackingUrl } from "@/lib/tracking-status";
import { whatsappSupportUrl } from "@/lib/contact-info";

interface ShipmentInfo {
  awb: string | null;
  courier: string | null;
  status: string | null;
  trackingUrl: string | null;
  shippedAt: string | null;
  deliveredAt: string | null;
  syncedAt: string | null;
}
interface ShipmentEvent {
  status: string | null;
  location: string | null;
  occurredAt: string | null;
  source: string | null;
}
interface OrderHead {
  name: string;
  status: string;
  fulfillmentStatus: string | null;
  placedAt: string | null;
  customerName: string | null;
  city: string | null;
}

const STAGES = [
  { key: "confirmed", label: "Confirmed", Icon: Check },
  { key: "packing", label: "Packing", Icon: Package },
  { key: "shipped", label: "Shipped", Icon: Truck },
  { key: "out", label: "Out for delivery", Icon: MapPin },
  { key: "delivered", label: "Delivered", Icon: Sparkles },
] as const;

function fmt(dt: string | null): string {
  if (!dt) return "";
  const d = new Date(dt);
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

// "3 minutes ago" style — used to make tracking freshness honest.
function relTime(dt: string | null): string {
  if (!dt) return "";
  const then = new Date(dt).getTime();
  if (!Number.isFinite(then)) return "";
  const diffMs = Date.now() - then;
  const mins = Math.max(0, Math.round(diffMs / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

const STALE_MS = 24 * 60 * 60 * 1000; // >24h since last webhook = stale
const SUPPORT_WA = whatsappSupportUrl("Hi TruWe, I need help with my order.");

export const Route = createFileRoute("/track-order")({
  head: () => ({
    meta: [
      { title: "Track your order. TruWe" },
      { name: "description", content: "Latest shipment updates and progress history for your TruWe order." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: TrackOrderPage,
});

function TrackOrderPage() {
  const [order, setOrder] = useState<OrderHead | null>(null);
  const [shipment, setShipment] = useState<ShipmentInfo | null>(null);
  const [events, setEvents] = useState<ShipmentEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderName, setOrderName] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<string | null>(null);

  // Lookup form (email or phone + order ID)
  const [formOrder, setFormOrder] = useState("");
  const [contactMode, setContactMode] = useState<"email" | "phone">("email");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [needsLookup, setNeedsLookup] = useState(false);

  async function fetchTracking(payload: {
    order: string;
    paymentId?: string;
    email?: string;
    phone?: string;
  }) {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/public/orders/track", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = (await res.json()) as {
        success?: boolean;
        order?: OrderHead;
        shipment?: ShipmentInfo | null;
        events?: ShipmentEvent[];
        error?: string;
      };
      if (!res.ok || !data.success || !data.order) {
        setError(
          data.error ||
            "We couldn't find that order. Double-check the order ID and the email or phone used at checkout.",
        );
      } else {
        setOrder(data.order);
        setShipment(data.shipment ?? null);
        setEvents(data.events || []);
        setOrderName(data.order.name);
        setNeedsLookup(false);
      }
    } catch (err: any) {
      setError(err?.message || "Tracking unavailable right now.");
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const name = params.get("order");
    const pid = params.get("pid") || params.get("payment_id");
    setOrderName(name);
    setPaymentId(pid);

    // Only auto-fetch when a payment id (a real shared secret from checkout) is
    // present. We intentionally do NOT auto-lookup with ?email= or ?phone= —
    // that would put customer PII in a shareable URL / referrer / history.
    if (name && pid) {
      fetchTracking({ order: name, paymentId: pid });
    } else {
      if (name) setFormOrder(name);
      setNeedsLookup(true);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const o = formOrder.trim();
    if (!o) {
      setError("Enter your order ID.");
      return;
    }
    if (contactMode === "email") {
      const em = formEmail.trim();
      if (!em) {
        setError("Enter the email used at checkout.");
        return;
      }
      setOrderName(o);
      fetchTracking({ order: o, email: em });
    } else {
      const ph = formPhone.trim();
      if (!ph) {
        setError("Enter the phone number used at checkout.");
        return;
      }
      setOrderName(o);
      fetchTracking({ order: o, phone: ph });
    }
  }


  const tracking = useMemo(
    () =>
      resolveTrackingState({
        orderStatus: order?.status ?? null,
        shipmentStatus: shipment?.status ?? null,
        latestEventStatus: events[0]?.status ?? null,
        fulfillmentStatus: order?.fulfillmentStatus ?? null,
      }),
    [shipment?.status, events, order?.fulfillmentStatus, order?.status],
  );
  const activeStage = tracking.stageIndex;
  const exception = tracking.exception;
  const isStale =
    !!shipment?.syncedAt && Date.now() - new Date(shipment.syncedAt).getTime() > STALE_MS;
  const carrierUrl = isSafeTrackingUrl(shipment?.trackingUrl) ? shipment!.trackingUrl : null;
  const firstName = order?.customerName?.split(" ")[0] || "";

  return (
    <main className="min-h-screen bg-gradient-to-b from-white via-[#F7F7F5] to-[#F7F7F5] pb-24 text-[#2C2C2C]">
      <section className="mx-auto w-full max-w-2xl px-5 pt-10 sm:pt-14">
        {paymentId ? (
          <Link
            to="/order-success"
            search={{ order: orderName, pid: paymentId } as any}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1E5EFF] hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Back to order
          </Link>
        ) : (
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1E5EFF] hover:underline"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        )}
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl"
        >
          Track your order{firstName ? `, ${firstName}` : ""}
        </motion.h1>
        <p className="mt-2 text-[15px] text-[#2C2C2C]/65">
          {order ? (
            <>
              Order{" "}
              <span className="font-mono font-semibold text-[#2C2C2C]">
                {order.name || orderName || "-"}
              </span>
              {order.city ? <> · Shipping to {order.city}</> : null}
            </>
          ) : (
            <>Enter your order ID with the email or phone used at checkout.</>
          )}
        </p>

        {needsLookup && !order && (
          <form
            onSubmit={onSubmit}
            className="mt-8 rounded-[24px] bg-white p-5 shadow-[0_6px_30px_rgba(20,30,60,0.05)] ring-1 ring-black/[0.04] sm:p-6"
          >
            <label className="block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#2C2C2C]/60">
                Order ID
              </span>
              <input
                type="text"
                value={formOrder}
                onChange={(e) => setFormOrder(e.target.value)}
                placeholder="TW-1042 or #1042"
                autoComplete="off"
                className="mt-1.5 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[15px] font-medium text-[#2C2C2C] outline-none transition focus:border-[#1E5EFF] focus:ring-2 focus:ring-[#1E5EFF]/15"
              />
            </label>

            <div className="mt-4 inline-flex rounded-full bg-[#F1F1EF] p-1 text-[13px] font-semibold">
              {(["email", "phone"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setContactMode(m);
                    setError(null);
                  }}
                  className={`rounded-full px-4 py-1.5 transition ${
                    contactMode === m
                      ? "bg-white text-[#1E5EFF] shadow-sm"
                      : "text-[#2C2C2C]/60 hover:text-[#2C2C2C]"
                  }`}
                >
                  {m === "email" ? "Email" : "Phone"}
                </button>
              ))}
            </div>

            {contactMode === "email" ? (
              <label className="mt-3 block">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#2C2C2C]/60">
                  Email used at checkout
                </span>
                <input
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  placeholder="you@email.com"
                  autoComplete="email"
                  className="mt-1.5 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[15px] font-medium text-[#2C2C2C] outline-none transition focus:border-[#1E5EFF] focus:ring-2 focus:ring-[#1E5EFF]/15"
                />
              </label>
            ) : (
              <label className="mt-3 block">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#2C2C2C]/60">
                  Phone used at checkout
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={formPhone}
                  onChange={(e) => setFormPhone(e.target.value)}
                  placeholder="+91 98765 43210"
                  autoComplete="tel"
                  className="mt-1.5 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[15px] font-medium text-[#2C2C2C] outline-none transition focus:border-[#1E5EFF] focus:ring-2 focus:ring-[#1E5EFF]/15"
                />
              </label>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1E5EFF] px-5 py-3 text-[15px] font-semibold text-white shadow-[0_10px_28px_rgba(30,94,255,0.28)] transition hover:brightness-110 disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Looking up…
                </>
              ) : (
                <>Track order</>
              )}
            </button>
            <p className="mt-3 text-center text-[12px] text-[#2C2C2C]/55">
              Your order ID is in your confirmation email and on the order success page.
            </p>
          </form>
        )}

        {loading && !needsLookup && (
          <div className="mt-10 flex items-center justify-center gap-2 text-sm text-[#2C2C2C]/60">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading tracking…
          </div>
        )}
        {!loading && error && (
          <p className="mt-6 rounded-2xl bg-white p-4 text-center text-sm text-[#2C2C2C]/70 ring-1 ring-black/5">
            {error}
          </p>
        )}
      </section>


      {/* EXCEPTION CARD, abnormal shipment states surface above the progress bar */}
      {order && exception && (
        <section className="mx-auto mt-6 w-full max-w-2xl px-5">
          <div className="rounded-[24px] bg-gradient-to-br from-[#FFF6EC] to-white p-5 shadow-[0_6px_30px_rgba(200,110,20,0.10)] ring-1 ring-[#F0B27A]/40 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F4A261]/20 text-[#B8620F]">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold text-[#2C2C2C]">{exception.label}</p>
                <p className="mt-1 text-[13.5px] leading-relaxed text-[#2C2C2C]/70">
                  {exception.description}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#1E5EFF] px-3.5 py-1.5 text-[12.5px] font-semibold text-white transition hover:brightness-110"
                  >
                    <Headphones className="h-3.5 w-3.5" /> Contact support
                  </Link>
                  <a
                    href={SUPPORT_WA}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[12.5px] font-semibold text-[#2C2C2C] ring-1 ring-black/10 transition hover:bg-[#F7F7F5]"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp us
                  </a>
                  {carrierUrl && (
                    <a
                      href={carrierUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[12.5px] font-semibold text-[#1E5EFF] ring-1 ring-[#1E5EFF]/20 transition hover:bg-[#1E5EFF]/5"
                    >
                      Carrier tracking <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* PROGRESS */}
      {order && (
        <section className="mx-auto mt-6 w-full max-w-2xl px-5">
          <div className="rounded-[28px] bg-white p-5 shadow-[0_6px_30px_rgba(20,30,60,0.05)] ring-1 ring-black/[0.04] sm:p-7">
            <div className="flex items-center justify-between gap-3">
              {STAGES.map((s, i) => {
                const done = i <= activeStage && !exception;
                const Icon = s.Icon;
                return (
                  <div key={s.key} className="flex flex-1 flex-col items-center text-center">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full ring-1 transition ${
                        done
                          ? "bg-[#1E5EFF] text-white ring-[#1E5EFF]"
                          : "bg-white text-[#2C2C2C]/40 ring-black/10"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <p
                      className={`mt-2 text-[10px] font-semibold uppercase tracking-wider sm:text-[11px] ${
                        done ? "text-[#2C2C2C]" : "text-[#2C2C2C]/40"
                      }`}
                    >
                      {s.label}
                    </p>
                  </div>
                );
              })}
            </div>
            {shipment?.syncedAt && (
              <p className="mt-4 text-center text-[11.5px] text-[#2C2C2C]/50">
                Last updated {relTime(shipment.syncedAt)} · {fmt(shipment.syncedAt)}
                {isStale ? " · Tracking may be delayed" : ""}
              </p>
            )}
          </div>

          {shipment && (shipment.courier || shipment.awb || carrierUrl || shipment.shippedAt || shipment.deliveredAt) && (
            <div className="mt-4 grid grid-cols-2 gap-3 rounded-[24px] bg-white p-5 shadow-[0_6px_30px_rgba(20,30,60,0.05)] ring-1 ring-black/[0.04] sm:grid-cols-4">
              <Stat label="Courier" value={shipment.courier || "Assigned soon"} />
              <Stat label="AWB" value={shipment.awb || "-"} mono />
              <Stat
                label="Shipped"
                value={shipment.shippedAt ? fmt(shipment.shippedAt) : "-"}
              />
              <Stat
                label="Delivered"
                value={shipment.deliveredAt ? fmt(shipment.deliveredAt) : "-"}
              />
              {carrierUrl && (
                <div className="col-span-2 sm:col-span-4">
                  <a
                    href={carrierUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[13.5px] font-semibold text-[#1E5EFF] hover:underline"
                  >
                    Open carrier tracking <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* HISTORY */}
      {order && (
        <section className="mx-auto mt-8 w-full max-w-2xl px-5">
          <header className="mb-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1E5EFF]/80">
              History
            </p>
            <h2 className="mt-1 font-display text-xl font-bold sm:text-2xl">Latest shipment updates</h2>
          </header>

          <div className="rounded-[28px] bg-white p-5 shadow-[0_6px_30px_rgba(20,30,60,0.05)] ring-1 ring-black/[0.04] sm:p-6">
            {events.length === 0 ? (
              <div className="flex items-center gap-3 text-sm text-[#2C2C2C]/60">
                <Clock className="h-4 w-4" />
                <span>
                  No carrier updates yet. We'll post new events here as soon as your shipment moves.
                </span>
              </div>
            ) : (
              <ol className="relative space-y-5 pl-6">
                <span className="absolute left-[7px] top-1 bottom-1 w-px bg-[#1E5EFF]/15" />
                {events.map((e, i) => (
                  <li key={i} className="relative">
                    <span
                      className={`absolute -left-[22px] top-1.5 h-3.5 w-3.5 rounded-full ring-2 ring-white ${
                        i === 0 ? "bg-[#1E5EFF]" : "bg-[#1E5EFF]/40"
                      }`}
                    />
                    <p className="text-[15px] font-semibold text-[#2C2C2C]">
                      {e.status || "Update"}
                    </p>
                    <p className="mt-0.5 text-[13px] text-[#2C2C2C]/60">
                      {[fmt(e.occurredAt), e.location].filter(Boolean).join(" · ")}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </section>
      )}
    </main>
  );
}

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#2C2C2C]/45">
        {label}
      </p>
      <p
        className={`mt-1 truncate text-[15px] font-semibold text-[#2C2C2C] ${
          mono ? "font-mono" : ""
        }`}
        title={value}
      >
        {value}
      </p>
    </div>
  );
}
